import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Header,
  Loader,
  Message,
  Button,
  Table,
  Dropdown,
  Icon,
} from "semantic-ui-react";
import HeaderSubHeader from "semantic-ui-react/dist/commonjs/elements/Header/HeaderSubheader";
import { Pagination } from "@components/Pagination";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export const AuditLogsOverview = ({
  allAuditLogs,
  allLoading,
  allError,
  allTotal,
  fetchAllAuditLogs,
  currentPage = 1,
  currentSize = 10,
  onPageChange,
  onPageSizeChange,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(false);
  const [isPermanentlyCollapsed, setIsPermanentlyCollapsed] = useState(false);

  const truncatedLogIds = useMemo(() => {
    return new Set(
      allAuditLogs
        .filter((log) => {
          const rawDetails =
            typeof log.details === "string"
              ? log.details
              : JSON.stringify(log.details, null, 2);
          return rawDetails.length > 30;
        })
        .map((log) => log.id)
    );
  }, [allAuditLogs]);

  useEffect(() => {
    if (isPermanentlyExpanded) {
      setExpandedRows(new Set(truncatedLogIds));
    } else if (isPermanentlyCollapsed) {
      setExpandedRows(new Set());
    }
  }, [
    allAuditLogs,
    truncatedLogIds,
    isPermanentlyExpanded,
    isPermanentlyCollapsed,
  ]);

  const toggleRowExpansion = (logId) => {
    setIsPermanentlyExpanded(false);
    setIsPermanentlyCollapsed(false);
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    if (isPermanentlyExpanded) {
      setIsPermanentlyExpanded(false);
      setIsPermanentlyCollapsed(true);
      setExpandedRows(new Set());
    } else {
      setIsPermanentlyExpanded(true);
      setIsPermanentlyCollapsed(false);
      setExpandedRows(new Set(truncatedLogIds));
    }
  };

  const getGlobalExpandState = () => {
    if (isPermanentlyExpanded) return "expanded";
    if (isPermanentlyCollapsed) return "collapsed";
    const allTruncatedExpanded =
      truncatedLogIds.size > 0 &&
      Array.from(truncatedLogIds).every((id) => expandedRows.has(id));
    return allTruncatedExpanded ? "expanded" : "collapsed";
  };

  const globalState = getGlobalExpandState();

  if (allLoading) {
    return <Loader active>Loading all audit logs...</Loader>;
  }

  if (allError) {
    return (
      <Message negative>
        <Message.Header>Error</Message.Header>
        <p>{allError}</p>
        <Button onClick={fetchAllAuditLogs}>Retry</Button>
      </Message>
    );
  }

  if (allAuditLogs.length === 0) {
    return (
      <Message info>
        <Message.Header>No audit logs found</Message.Header>
        <p>No audit activity has been recorded yet.</p>
      </Message>
    );
  }

  const startIndex = (currentPage - 1) * currentSize;
  const endIndex = Math.min(startIndex + currentSize, allTotal);

  const handleExportClick = async (format = "csv") => {
    if (format === "pdf") {
      const desiredColumns = ["Action", "Details", "Created By", "Created At"];
      const tableHeaders = desiredColumns.map((h) => `<th>${h}</th>`).join("");
      const tableRows = allAuditLogs
        .map((log) => {
          const cells = [
            log.action,
            typeof log.details === "string"
              ? log.details
              : JSON.stringify(log.details, null, 2),
            log.created_by || "System",
            log.created_at
              ? new Date(log.created_at).toLocaleString()
              : "Unknown",
          ]
            .map((val) => `<td>${val}</td>`)
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      const htmlContent = generateExportHTML({
        reportTitle: "Audit Logs Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        showUniversityLogo: true,
        showLrcLogo: true,
        fallbackLogoText: "Library Resource Center",
      });
      const blob = new Blob([htmlContent], {
        type: "text/html;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }
      return;
    }

    const csvHeaders = ["Action", "Details", "Created By", "Created At"];
    const csvRows = allAuditLogs.map((log) => [
      log.action,
      typeof log.details === "string"
        ? log.details.replace(/[\r\n]+/g, " ")
        : JSON.stringify(log.details),
      log.created_by || "System",
      log.created_at ? new Date(log.created_at).toLocaleString() : "Unknown",
    ]);
    const csvContent = [
      csvHeaders.join(","),
      ...csvRows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `audit_logs_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const pageSizeOptions = [
    { key: 5, text: "5", value: 5 },
    { key: 10, text: "10", value: 10 },
    { key: 20, text: "20", value: 20 },
    { key: 50, text: "50", value: 50 },
    { key: 100, text: "100", value: 100 },
  ];

  return (
    <Container fluid className="spaced">
      <div style={{ marginBottom: "1rem" }}>
        <Header as="h3">All Audit Logs</Header>
        <HeaderSubHeader>
          {allTotal} logs found - Showing {startIndex + 1} to {endIndex} of{" "}
          {allTotal}
        </HeaderSubHeader>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.5rem",
        }}
      >
        {truncatedLogIds.size > 0 && (
          <Button
            basic={!isPermanentlyExpanded && !isPermanentlyCollapsed}
            primary={isPermanentlyExpanded}
            secondary={isPermanentlyCollapsed}
            size="small"
            onClick={handleExpandAll}
            title={
              globalState === "expanded"
                ? "Collapse all details permanently"
                : "Expand all details permanently"
            }
            aria-label={
              globalState === "expanded"
                ? "Collapse all details permanently"
                : "Expand all details permanently"
            }
          >
            <Icon name={globalState === "expanded" ? "compress" : "expand"} />
            {globalState === "expanded" ? "Collapse All" : "Expand All"}
          </Button>
        )}
        {truncatedLogIds.size === 0 && <div />}
        <ExportReactSearchKitResults
          exportBaseUrl=""
          onExportClick={handleExportClick}
          options={[
            { key: "csv", text: "Export CSV", value: "csv" },
            { key: "pdf", text: "Export PDF (HTML)", value: "pdf" },
          ]}
        />
      </div>
      <Table
        celled
        striped
        compact
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ width: "5%" }}>#</Table.HeaderCell>
            <Table.HeaderCell style={{ width: "10%" }}>
              User Action
            </Table.HeaderCell>
            <Table.HeaderCell style={{ width: "50%" }}>
              Details
            </Table.HeaderCell>
            <Table.HeaderCell style={{ width: "15%" }}>
              Created By
            </Table.HeaderCell>
            <Table.HeaderCell style={{ width: "20%" }}>
              Created At
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {allAuditLogs.map((log, index) => {
            const rawDetails =
              typeof log.details === "string"
                ? log.details
                : JSON.stringify(log.details, null, 2);
            const isTruncated = rawDetails.length > 30;
            const isExpanded = isPermanentlyExpanded
              ? isTruncated
              : isPermanentlyCollapsed
              ? false
              : expandedRows.has(log.id);
            const displayDetails =
              isTruncated && !isExpanded
                ? `${rawDetails.slice(0, 30)}...`
                : rawDetails;
            return (
              <Table.Row key={log.id}>
                <Table.Cell>{startIndex + index + 1}</Table.Cell>
                <Table.Cell>{log.action}</Table.Cell>
                <Table.Cell>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.5rem",
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        maxHeight: isExpanded ? "none" : "200px",
                        overflow: isExpanded ? "visible" : "auto",
                        margin: 0,
                      }}
                    >
                      {displayDetails}
                    </pre>
                    {isTruncated && (
                      <Icon
                        name={isExpanded ? "chevron up" : "chevron down"}
                        link
                        size="small"
                        onClick={() => toggleRowExpansion(log.id)}
                        title={
                          isExpanded ? "Collapse details" : "Expand details"
                        }
                        style={{
                          cursor: "pointer",
                          color:
                            isPermanentlyExpanded || isPermanentlyCollapsed
                              ? "#999"
                              : "#4183c4",
                          margin: 0,
                          padding: "0.2em",
                          alignSelf: "flex-start",
                          opacity:
                            isPermanentlyExpanded || isPermanentlyCollapsed
                              ? 0.5
                              : 1,
                        }}
                        aria-label={
                          isExpanded ? "Collapse details" : "Expand details"
                        }
                      />
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>{log.created_by || "System"}</Table.Cell>
                <Table.Cell>
                  {log.created_at
                    ? new Date(log.created_at).toLocaleString()
                    : "Unknown"}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {allTotal > currentSize && (
        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
          }}
        >
          <Pagination
            currentPage={currentPage}
            currentSize={currentSize}
            totalResults={allTotal}
            onPageChange={onPageChange}
            loading={allLoading}
          />
          <Dropdown
            selection
            options={pageSizeOptions}
            value={currentSize}
            onChange={(e, { value }) => onPageSizeChange(value)}
            compact
            title="Rows per page"
            aria-label="Rows per page"
          />
        </div>
      )}
    </Container>
  );
};

AuditLogsOverview.propTypes = {
  allAuditLogs: PropTypes.array.isRequired,
  allLoading: PropTypes.bool.isRequired,
  allError: PropTypes.string,
  allTotal: PropTypes.number.isRequired,
  fetchAllAuditLogs: PropTypes.func.isRequired,
  currentPage: PropTypes.number,
  currentSize: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};
